import React, {
  useEffect,
  useMemo,
  useDeferredValue,
  useCallback,
  useRef,
  useState,
  memo,
} from "react";
import { MapContainer, GeoJSON, useMap, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

// ---------- Leaflet Icon Fix ----------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

/* =========================================================
   Utils comunes
========================================================= */
const MONTHS_2025 = [
  "2025_01","2025_02","2025_03","2025_04","2025_05","2025_06",
  "2025_07","2025_08","2025_09","2025_10","2025_11","2025_12",
];
const MONTH_LABELS_ES = [
  "enero","febrero","marzo","abril","mayo","junio",
  "julio","agosto","setiembre","octubre","noviembre","diciembre",
];

const parseYYYY_MM = (s) => {
  const [y, m] = (s || "").split("_").map(Number);
  if (!y || !m) return { year: 2025, monthIndex0: 0 };
  return { year: y, monthIndex0: m - 1 };
};

const formatYYYY_MM_human = (s) => {
  const { year, monthIndex0 } = parseYYYY_MM(s);
  return `${MONTH_LABELS_ES[monthIndex0]} ${year}`;
};

const formatMonthShort = (s) => {
  const { monthIndex0 } = parseYYYY_MM(s);
  return MONTH_LABELS_ES[monthIndex0].substring(0, 3);
};

const densifyMonths2025 = (rows) => {
  const byMes = new Map();
  (rows || []).forEach((r) => byMes.set(r.mes, r));
  return MONTHS_2025.map((mes) => {
    if (byMes.has(mes)) return byMes.get(mes);
    const { year, monthIndex0 } = parseYYYY_MM(mes);
    return {
      mes,
      comprension_lectora: 0,
      lecturas_distintas: 0,
      inorm: 0,
      days_with_lectures: 0,
      _empty: true,
      _humanMes: `${MONTH_LABELS_ES[monthIndex0]} ${year}`,
    };
  });
};

const toRGBA = (hex, a = 0.7) => {
  const h = (hex || "#000").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const bigint = parseInt(full || "000000", 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${a})`;
};

/* =========================================================
   MetricChart MENSUAL
========================================================= */
function MonthlyMetricChart({ datosMensuales, metricFilter, metricas, viewType = "bar" }) {
  const chartRef = useRef(null);

  // Densifica meses 2025 con ceros cuando no hay fila
  const densified = useMemo(() => {
    const map = new Map(
      (Array.isArray(datosMensuales) ? datosMensuales : [])
        .filter(r => r && r.mes)
        .map(r => [r.mes, r])
    );
    return MONTHS_2025.map(mes => {
      const r = map.get(mes);
      return r || {
        mes,
        comprension_lectora: 0,
        inorm: 0,
        lecturas_distintas: 0,
        days_with_lectures: 0,
        _empty: true,
      };
    });
  }, [datosMensuales]);

  const {
    labels,
    values,
    metainfo: { colorHex, label, formatter, maxY },
  } = useMemo(() => {
    const isPercent = metricFilter === "comprension_lectora" || metricFilter === "inorm";

    const labels = MONTHS_2025.map((m) => formatMonthShort(m));
    const values = MONTHS_2025.map((m) => {
      const row = densified.find(r => r.mes === m) || {};
      const raw = Number(row?.[metricFilter] ?? 0);
      return isPercent ? raw * 100 : raw;
    });

    const vmax = values.length ? Math.max(...values.map((v) => Number(v) || 0)) : 0;
    const computedMaxY = isPercent ? 100 : Math.max(10, Math.ceil((vmax || 0) * 1.15));

    const met = metricas?.[metricFilter] || {};
    const colorHex = met.colorHex || "#111827";
    const label = met.label || metricFilter;
    const formatter =
      met.format ||
      ((v) => (isPercent ? `${Number(v).toFixed(1)}%` : (Number.isInteger(v) ? v : Number(v).toFixed(1))));

    return { labels, values, metainfo: { colorHex, label, formatter, maxY: computedMaxY } };
  }, [densified, metricFilter, metricas]);

  const commonOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1F2937",
          titleColor: "#F9FAFB",
          bodyColor: "#F9FAFB",
          borderColor: "rgba(99,102,241,0.2)",
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          cornerRadius: 8,
          callbacks: {
            title: (items) => {
              const i = items?.[0]?.dataIndex ?? 0;
              const mes = MONTHS_2025[i] ?? "2025_01";
              return formatYYYY_MM_human(mes);
            },
            label: (ctx) => {
              const v = Number(ctx.parsed.y ?? 0);
              return `${label}: ${Number.isFinite(v) ? (maxY === 100 ? v.toFixed(1) + "%" : (Number.isInteger(v) ? v : v.toFixed(1))) : "0"}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: { autoSkip: false, maxRotation: 45, minRotation: 45, color: "#6B7280", font: { size: 11 } },
        },
        y: {
          min: 0,
          max: maxY,
          grid: { color: maxY === 100 ? "rgba(15,23,42,0.12)" : "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: {
            color: "#6B7280",
            font: { size: 11 },
            callback: (v) => (maxY === 100 ? `${v}%` : v),
          },
          border: { display: false },
        },
      },
    }),
    [label, maxY]
  );

  const buildGradient = (ctx, baseColor) => {
    const ca = ctx?.chart?.chartArea;
    const c = ctx?.chart?.ctx;
    if (!ca || !c) return baseColor;
    const g = c.createLinearGradient(0, ca.top, 0, ca.bottom);
    g.addColorStop(0, toRGBA(baseColor, viewType === "line" ? 0.4 : 0.7));
    g.addColorStop(1, toRGBA(baseColor, viewType === "line" ? 0.1 : 0.15));
    return g;
  };

  const dataset =
    viewType === "line"
      ? {
          type: "line",
          label,
          data: values,
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          borderColor: colorHex,
          backgroundColor: (ctx) => buildGradient(ctx, colorHex),
          pointRadius: 4,
          pointBackgroundColor: colorHex,
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointHoverRadius: 8,
          pointHitRadius: 12,
        }
      : {
          type: "bar",
          label,
          data: values,
          backgroundColor: (ctx) => buildGradient(ctx, colorHex),
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 32,
          hoverBackgroundColor: toRGBA(colorHex, 0.9),
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        };

  return (
    <div className="h-64 sm:h-72 md:h-80 lg:h-96 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      {viewType === "line" ? (
        <Line ref={chartRef} data={{ labels, datasets: [dataset] }} options={commonOptions} />
      ) : (
        <Bar ref={chartRef} data={{ labels, datasets: [dataset] }} options={commonOptions} />
      )}
    </div>
  );
}

/* =========================================================
   MetricChart DIARIO
========================================================= */
const weekendPlugin = {
  id: "weekendBackground",
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const indexes = chart.options.weekendIndexes || [];
    if (!chartArea || !indexes?.length) return;
    ctx.save();
    const xScale = scales.x;
    indexes.forEach((i) => {
      const left = xScale.getPixelForValue(i - 0.5);
      const right = xScale.getPixelForValue(i + 0.5);
      const width = right - left;
      ctx.fillStyle = "rgba(99,102,241,0.08)";
      ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
      ctx.strokeStyle = "rgba(99,102,241,0.15)";
      ctx.lineWidth = 1;
      ctx.strokeRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
    });
    ctx.restore();
  },
};

const daysInMonth = (year, monthIndex0) => new Date(year, monthIndex0 + 1, 0).getDate();

const densifyByDay = (datos, metricKey, year, monthIndex0, toDay) => {
  const dim = daysInMonth(year, monthIndex0);
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() === monthIndex0;
  const limitDay = Math.min(dim, typeof toDay === "number" ? toDay : isCurrentMonth ? now.getDate() : dim);

  const map = {};
  for (const d of datos || []) {
    const raw = Number(d?.[metricKey] ?? 0);
    const dia = Number(d?.dia ?? d?.dia_leido_del_mes ?? 0);
    if (dia >= 1 && dia <= dim) map[dia] = raw;
  }

  const labels = [];
  const values = [];
  const weekendIdx = [];
  for (let day = 1; day <= limitDay; day++) {
    const date = new Date(year, monthIndex0, day);
    labels.push(
      date
        .toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })
        .replace(",", "")
    );
    values.push(map[day] ?? 0);
    const dow = date.getDay();
    if (dow === 0 || dow === 6) weekendIdx.push(day - 1);
  }
  const isEmptyMonth = values.every((v) => Number(v) === 0);
  return { labels, values, weekendIdx, isEmptyMonth };
};

function DailyMetricChart({
  datosProcesados,
  metricFilter,
  metricas,
  viewType = "bar",
  maxValor,
  yearMonth, // {year, monthIndex0, toDay?}
}) {
  const chartRef = useRef(null);
  const today = new Date();
  const targetYear = (yearMonth && yearMonth.year) || today.getFullYear();
  const targetMonth0 = (yearMonth && yearMonth.monthIndex0) || today.getMonth();
  const toDayLimit = yearMonth?.toDay;

  const {
    labels,
    values,
    weekendIdx,
    isEmptyMonth,
    metainfo: { colorHex, label, formatter, maxY },
  } = useMemo(() => {
    const { labels, values, weekendIdx, isEmptyMonth } = densifyByDay(
      datosProcesados,
      metricFilter,
      targetYear,
      targetMonth0,
      toDayLimit
    );

    const vmax = values.length ? Math.max(...values.map((v) => Number(v) || 0)) : 0;
    const isPercentScale =
      metricFilter === "comprension_lectora" ||
      metricFilter === "inorm" ||
      metricFilter.toLowerCase().includes("norm");

    const converted = values.map((v) => (isPercentScale ? Number(v) * 100 : Number(v)));
    const computedMaxY = Number.isFinite(maxValor)
      ? maxValor
      : isPercentScale
      ? 100
      : Math.ceil((vmax || 0) * 1.15) || 10;

    const met = metricas?.[metricFilter] || {};
    const baseColorHex = isEmptyMonth ? "#9CA3AF" : met.colorHex || "#111827";
    const label = met.label || metricFilter;
    const formatter =
      typeof met.format === "function"
        ? met.format
        : (v) => (isPercentScale ? `${Number(v).toFixed(1)}%` : (Number.isInteger(v) ? v : Number(v).toFixed(1)));

    return {
      labels,
      values: converted,
      weekendIdx,
      isEmptyMonth,
      metainfo: { colorHex: baseColorHex, label, formatter, maxY: computedMaxY },
    };
  }, [datosProcesados, metricFilter, metricas, maxValor, targetYear, targetMonth0, toDayLimit]);

  const commonOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: isEmptyMonth ? "#6B7280" : "#1F2937",
          titleColor: "#F9FAFB",
          bodyColor: "#F9FAFB",
          borderColor: isEmptyMonth ? "rgba(156,163,175,0.2)" : "rgba(99,102,241,0.2)",
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          cornerRadius: 8,
          callbacks: {
            title: (items) => {
              const i = items?.[0]?.dataIndex ?? 0;
              const date = new Date(targetYear, targetMonth0, i + 1);
              const dayName = date.toLocaleDateString("es-ES", { weekday: "long" });
              const isWeekend = weekendIdx.includes(i);
              const weekendIcon = isWeekend ? " 🏖️" : "";
              return `${dayName} ${date.getDate()} de ${date.toLocaleDateString("es-ES", { month: "long" })}${weekendIcon}`;
            },
            label: (ctx) => {
              const value = Number(ctx.parsed.y ?? 0);
              if (isEmptyMonth) return `${label}: Sin lecturas este día`;
              const show = Number.isFinite(value) ? (maxY === 100 ? value.toFixed(1) + "%" : (Number.isInteger(value) ? value : value.toFixed(1))) : "0";
              const hasValue = value > 0;
              const icon = hasValue ? "📖" : "⏸️";
              return `${label}: ${show} ${icon}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: isEmptyMonth ? "rgba(156,163,175,0.06)" : "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: {
            autoSkip: false,
            maxRotation: 45,   // ← inclinado para ver todo el mes
            minRotation: 45,
            color: (ctx) => (isEmptyMonth ? "#9CA3AF" : weekendIdx.includes(ctx.index) ? "#6366F1" : "#6B7280"),
            font: { size: 11 },
          },
        },
        y: {
          min: 0,
          max: maxY,
          grid: { color: maxY === 100 ? "rgba(15,23,42,0.12)" : "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: {
            color: isEmptyMonth ? "#9CA3AF" : "#6B7280",
            font: { size: 11 },
            callback: (v) => (maxY === 100 ? `${v}%` : v),
          },
          border: { display: false },
        },
      },
      weekendIndexes: weekendIdx,
    }),
    [label, maxY, weekendIdx, targetYear, targetMonth0, isEmptyMonth]
  );

  const buildGradient = (ctx, baseColor) => {
    const ca = ctx?.chart?.chartArea;
    const c = ctx?.chart?.ctx;
    if (!ca || !c) return baseColor;
    const g = c.createLinearGradient(0, ca.top, 0, ca.bottom);
    if (isEmptyMonth) {
      g.addColorStop(0, toRGBA("#9CA3AF", 0.3));
      g.addColorStop(1, toRGBA("#9CA3AF", 0.1));
    } else {
      g.addColorStop(0, toRGBA(baseColor, viewType === "line" ? 0.4 : 0.7));
      g.addColorStop(1, toRGBA(baseColor, viewType === "line" ? 0.1 : 0.15));
    }
    return g;
  };

  const data =
    viewType === "line"
      ? {
          labels,
          datasets: [
            {
              type: "line",
              label,
              data: values,
              fill: true,
              tension: 0.4,
              borderWidth: isEmptyMonth ? 2 : 3,
              borderColor: colorHex,
              borderDash: isEmptyMonth ? [5, 5] : [],
              backgroundColor: (ctx) => buildGradient(ctx, colorHex),
              pointRadius: (ctx) => (isEmptyMonth ? 0 : (weekendIdx.includes(ctx.dataIndex) ? 5 : 3)),
              pointBackgroundColor: (ctx) => (isEmptyMonth ? "#9CA3AF" : (weekendIdx.includes(ctx.dataIndex) ? "#6366F1" : colorHex)),
              pointBorderColor: "#FFFFFF",
              pointBorderWidth: 2,
              pointHoverRadius: 8,
              pointHitRadius: 12,
            },
          ],
        }
      : {
          labels,
          datasets: [
            {
              type: "bar",
              label,
              data: values,
              backgroundColor: (ctx) => buildGradient(ctx, colorHex),
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: 24,
              hoverBackgroundColor: isEmptyMonth ? toRGBA("#9CA3AF", 0.5) : toRGBA(colorHex, 0.9),
              categoryPercentage: 0.8,
              barPercentage: 0.9,
            },
          ],
        };

  return (
    <div className={`h-64 sm:h-72 md:h-80 lg:h-96 rounded-xl border ${isEmptyMonth ? "border-gray-300 bg-gray-50" : "border-gray-200 bg-white"} p-4 shadow-sm`}>
      {viewType === "line" ? (
        <Line ref={chartRef} data={data} options={commonOptions} plugins={[weekendPlugin]} />
      ) : (
        <Bar ref={chartRef} data={data} options={commonOptions} plugins={[weekendPlugin]} />
      )}
    </div>
  );
}

/* =========================================================
   MonthlyReaderPanel
========================================================= */
function MonthlyReaderPanel({
  dataUrl,
  dataUrlDiaria,
  externalColegio = "",
  defaultNivel = "todos",
  nomProv,
}) {
  // Carga
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const [loadingDiario, setLoadingDiario] = useState(true);
  const [errorDiario, setErrorDiario] = useState(null);
  const [rowsDiario, setRowsDiario] = useState([]);

  // Filtros
  const [nivel, setNivel] = useState(defaultNivel);
  const [colegio, setColegio] = useState("");
  const [distrito, setDistrito] = useState("");
  const [gradoHR, setGradoHR] = useState("todos"); // ← grado_humanreadable
  const [qUser, setQUser] = useState("");
  const qUserDeferred = useDeferredValue(qUser);

  // UI
  const [tab, setTab] = useState("resumen");
  const [metric, setMetric] = useState("comprension_lectora");
  const [viewType, setViewType] = useState("bar");
  const [showUserList, setShowUserList] = useState(false);

  // selección por ID + nombre visible
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);


  // helpers tiempo actual
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth0 = now.getMonth();
  const MONTH_KEY = `2025-${String(currentMonth0 + 1).padStart(2, "0")}`; // si usas 2025 fijo; ajusta a currentYear si tu dataset cambia de año
  const monthShortEs = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"][currentMonth0];
  const mesActualEtiqueta = `${monthShortEs}-${currentYear}`;


  

  // Métricas
  const METRICAS = {
    comprension_lectora: { label: "Comprensión Lectora", colorHex: "#0ea5e9", format: (v) => `${(v * 100).toFixed(1)}%` },
    inorm: { label: "Hábito Lector", colorHex: "#22c55e", format: (v) => `${(v * 100).toFixed(1)}%` },
    lecturas_distintas: { label: "Lecturas Distintas", colorHex: "#8b5cf6", format: (v) => (Number.isInteger(v) ? v : v.toFixed(1)) },
    days_with_lectures: { label: "Días Leídos", colorHex: "#f59e0b", format: (v) => (Number.isInteger(v) ? v : v.toFixed(1)) },
  };

  const displayName = (given, family) => {
    const initials = (family || "")
      .trim().split(/\s+/).filter(Boolean)
      .map(w => (w[0] || "").toUpperCase()).join(".");
    return `${given || "—"}${initials ? " " + initials + "." : ""}`;
  };

  // Colegio externo
  useEffect(() => {
    if (externalColegio) {
      setColegio(externalColegio);
      setShowUserList(true);
      setSelectedUserName(null)
      setTab('resumen')
    }
  }, [externalColegio]);

  // Fetch mensual
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true); setError(null);
        const res = await fetch(dataUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        const filtered = nomProv
          ? (data || []).filter(r => (r.zoneinfo || "").toUpperCase() === String(nomProv).toUpperCase())
          : (data || []);
        setRows(filtered);
      } catch (e) {
        setError(e.message || "Error cargando datos (mensual)");
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dataUrl, nomProv]);

  // Fetch diario
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingDiario(true); setErrorDiario(null);
        const res = await fetch(dataUrlDiaria);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        const filtered = nomProv
          ? (data || []).filter(r => (r.zoneinfo || "").toUpperCase() === String(nomProv).toUpperCase())
          : (data || []);
        setRowsDiario(filtered);
      } catch (e) {
        setErrorDiario(e.message || "Error cargando datos (diario)");
      } finally {
        setLoadingDiario(false);
      }
    })();
    return () => { alive = false; };
  }, [dataUrlDiaria, nomProv]);

  // Opciones de filtros (basadas en mensual)
  const colegios = useMemo(() =>
    [...new Set(rows.map(r => r.colegio).filter(Boolean))].sort(), [rows]);

  const distritos = useMemo(() =>
    [...new Set(rows.map(r => r.locale).filter(Boolean))].sort(), [rows]);

  const niveles = useMemo(() =>
    ["todos", ...[...new Set(rows.map(r => r.nivel).filter(Boolean))].sort()], [rows]);

  // grado_humanreadable ordenado por "grado" numérico
  const gradosHR = useMemo(() => {
    const map = new Map(); // key: grado_humanreadable -> min grado numérico
    for (const r of rows) {
      const hr = r.grado_humanreadable || null;
      if (!hr) continue;
      const gnum = Number(r.grado);
      const cur = map.get(hr);
      if (!Number.isNaN(gnum)) {
        if (cur == null || gnum < cur) map.set(hr, gnum);
      } else if (!map.has(hr)) {
        map.set(hr, Number.POSITIVE_INFINITY); // sin número al final
      }
    }
    const arr = Array.from(map.entries())
      .map(([hr, g]) => ({ hr, g }))
      .sort((a, b) => (a.g === b.g ? a.hr.localeCompare(b.hr) : a.g - b.g))
      .map(x => x.hr);
    return ["todos", ...arr];
  }, [rows]);

  // Filtrado mensual
  const filteredRows = useMemo(() => {
    let arr = rows;
    if (nivel !== "todos")  arr = arr.filter(r => String(r.nivel).toUpperCase() === String(nivel).toUpperCase());
    if (colegio)            arr = arr.filter(r => String(r.colegio) === String(colegio));
    if (distrito)           arr = arr.filter(r => String(r.locale) === String(distrito));
    if (gradoHR !== "todos") arr = arr.filter(r => String(r.grado_humanreadable) === String(gradoHR));
    const term = (qUserDeferred || "").trim().toLowerCase();
    if (term) arr = arr.filter(r => (r.given_name || "").toLowerCase().includes(term));
    return arr;
  }, [rows, nivel, colegio, distrito, gradoHR, qUserDeferred]);

  // Filtrado diario
  const filteredRowsDiario = useMemo(() => {
    let arr = rowsDiario;
    if (nivel !== "todos")  arr = arr.filter(r => String(r.nivel).toUpperCase() === String(nivel).toUpperCase());
    if (colegio)            arr = arr.filter(r => String(r.colegio) === String(colegio));
    if (distrito)           arr = arr.filter(r => String(r.locale) === String(distrito));
    if (gradoHR !== "todos") arr = arr.filter(r => String(r.grado_humanreadable) === String(gradoHR));
    const term = (qUserDeferred || "").trim().toLowerCase();
    if (term) arr = arr.filter(r => (r.given_name || "").toLowerCase().includes(term));
    return arr;
  }, [rowsDiario, nivel, colegio, distrito, gradoHR, qUserDeferred]);

  // Resumen mensual (grupal)
  const resumenMensual = useMemo(() => {
    const byMes = {};
    filteredRows.forEach(r => {
      console.log(r)
      var mes = r.mes;
      const [y, m] = mes.split("-")
      mes = `${y}_${String(m??'').padStart(2, "0")}`
      console.log(mes)
      if (!byMes[mes]) byMes[mes] = { mes, comprension_lectora: 0, inorm: 0, lecturas_distintas: 0, days_with_lectures: 0, count: 0 };
      byMes[mes].comprension_lectora += Number(r.comprension_lectora || 0);
      byMes[mes].inorm += Number(r.inorm || 0);
      byMes[mes].lecturas_distintas += Number(r.lecturas_distintas || 0);
      byMes[mes].days_with_lectures += Number(r.days_with_lectures || 0);
      byMes[mes].count += 1;
    });
    let rowsAgg = Object.values(byMes).map(o => ({
      mes: o.mes,
      comprension_lectora: o.count ? o.comprension_lectora / o.count : 0,
      inorm: o.count ? o.inorm / o.count : 0,
      lecturas_distintas: o.count ? o.lecturas_distintas / o.count : 0,
      days_with_lectures: o.count ? o.days_with_lectures / o.count : 0,
    }));
    rowsAgg = densifyMonths2025(rowsAgg);
    rowsAgg.sort((a,b) => MONTHS_2025.indexOf(a.mes) - MONTHS_2025.indexOf(b.mes));
    return rowsAgg;
  }, [filteredRows]);

  // Series usuario (mensual y diario) por ID
  const toYYYY_MM = s => {
    if (!s) return "";
    const str = String(s);
    if (/^\d{4}_\d{2}$/.test(str)) return str;
    const m = str.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?/);
    if (m) return `${m[1]}_${m[2].padStart(2,"0")}`;
    const d = new Date(str);
    return isNaN(d) ? "" : `${d.getFullYear()}_${String(d.getMonth()+1).padStart(2,"0")}`;
  };
  const userSeries = useMemo(() =>
    selectedUserId
      ? filteredRows
          .filter(r => String(r.usuario_id ?? r.user_id ?? r.id_usuario ?? r.id ?? "") === String(selectedUserId))
          .map(r => ({ ...r, mes: toYYYY_MM(r.mes ?? r.mes_carga ?? r.datetime_exam) }))
      : []
  , [filteredRows, selectedUserId]);

  // Series usuario ( diario) por ID
  const userSeriesDiario = useMemo(() => {
    if (!selectedUserId) return [];
    return filteredRowsDiario.filter(r =>
      String(r.usuario_id ?? r.user_id ?? r.id_usuario ?? r.id ?? "") === String(selectedUserId)
    );
  }, [filteredRowsDiario, selectedUserId]);

  // UI helpers
  const MetricChip = ({ k }) => (
    <button
      onClick={() => setMetric(k)}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        metric === k ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
      }`}
    >
      {METRICAS[k].label}
    </button>
  );

  const monthAvgValue = (row, key) => {
    const v = Number(row?.[key] ?? 0);
    return key === "comprension_lectora" || key === "inorm"
      ? `${(v * 100).toFixed(1)}%`
      : (Number.isInteger(v) ? v : v.toFixed(1));
  };

  // ---- RENDER ----
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Filtros */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Nivel</label>
            <select className="w-full border rounded-md px-3 py-1.5 text-sm" value={nivel} onChange={(e) => setNivel(e.target.value)}>
              {niveles.map(n => <option key={n} value={n}>{n === "todos" ? "Todos" : n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Colegio</label>
            <select
              className="w-full border rounded-md px-3 py-1.5 text-sm"
              value={colegio}
              onChange={(e) => { setColegio(e.target.value); setShowUserList(!!e.target.value); }}
            >
              <option value="">Todos</option>
              {colegios.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Distrito</label>
            <select className="w-full border rounded-md px-3 py-1.5 text-sm" value={distrito} onChange={(e) => setDistrito(e.target.value)}>
              <option value="">Todos</option>
              {distritos.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Grado (visible)</label>
            <select className="w-full border rounded-md px-3 py-1.5 text-sm" value={gradoHR} onChange={(e) => setGradoHR(e.target.value)}>
              {gradosHR.map(g => <option key={g} value={g}>{g === "todos" ? "Todos" : g}</option>)}
            </select>
          </div>
        </div>

        {/* Búsqueda y controles */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Buscar usuario</label>
            <input className="w-full border rounded-md px-3 py-1.5 text-sm" placeholder="Ej. MARIA, JUAN..."
              value={qUser} onChange={(e) => setQUser(e.target.value)} />
          </div>
          <div className="flex items-end gap-2">
            <button onClick={() => setShowUserList(!showUserList)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors">
              {showUserList ? "👁️ Ocultar Usuarios" : "👥 Mostrar Usuarios"}
            </button>
          </div>
        </div>

        {/* chips de métricas y vista */}
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.keys(METRICAS).map(k => <MetricChip k={k} key={k} />)}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">Vista</span>
            <button onClick={() => setViewType("bar")}
              className={`px-2 py-1 rounded border text-xs ${viewType === "bar" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white"}`}>
              Barras
            </button>
            <button onClick={() => setViewType("line")}
              className={`px-2 py-1 rounded border text-xs ${viewType === "line" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white"}`}>
              Línea
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-2 px-4">
          {["resumen","usuarios","diario"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
                tab === t ? "border-blue-600 text-blue-700 bg-blue-50" : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}>
              {t === "resumen" ? "📊 Resumen Mensual" : t === "usuarios" ? "👤 Vista de Usuario mensual" : "👤 Vista de Usuario diario"}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4">
        <div className="grid grid-cols-1 gap-4">
          {/* Panel gráfico */}
          <div>
            {tab === "resumen" ? (
              loading ? (
                <div className="h-64 rounded-xl border border-gray-200 bg-white flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Cargando datos…</p>
                  </div>
                </div>
              ) : error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                  <div className="text-red-600 font-medium mb-1">Error</div>
                  <div className="text-red-700 text-sm">{error}</div>
                </div>
              ) : (
                <>
                  <div className="mb-4 text-center">
                    <h3 className="mt-1 text-base md:text-lg font-medium text-blue-600">
                      Evolución Mensual GRUPAL — {METRICAS[metric].label}
                    </h3>
                    <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-blue-700">
                      {colegio|| "Todos los colegios"}{distrito && ` • ${distrito}`}{gradoHR !== "todos" && ` • ${gradoHR}`}
                    </h3>
                    
                  </div>

                  <MonthlyMetricChart datosMensuales={resumenMensual} metricFilter={metric} metricas={METRICAS} viewType={viewType} />
                  {/* tabla detalle igual a la tuya */}
                </>
              )
            ) : tab === "usuarios" ? (
              <>
                <div className="mb-4">
                  {selectedUserName ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-800">Evolución Mensual — {selectedUserName}</h3>
                      <p className="text-sm text-gray-600">{METRICAS[metric].label} • {colegio || "Sin colegio específico"}</p>
                    </>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-4xl mb-3">👤</div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Selecciona un usuario</h3>
                      <p className="text-gray-600 text-sm">Haz clic en un usuario de la lista para ver su evolución mensual</p>
                    </div>
                  )}
                </div>
                {selectedUserName && (
                  <MonthlyMetricChart datosMensuales={userSeries} metricFilter={metric} metricas={METRICAS} viewType={viewType} />
                )}
              </>
            ) : (
              <>
                <div className="mb-4">
                  {selectedUserName ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-800">Progreso Diario (mes actual) — {selectedUserName}</h3>
                      <p className="text-sm text-gray-600">{METRICAS[metric].label} • {colegio || "Sin colegio específico"}</p>
                    </>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-4xl mb-3">👤</div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Selecciona un usuario</h3>
                      <p className="text-gray-600 text-sm">Haz clic en un usuario de la lista para ver su progreso diario del mes actual</p>
                    </div>
                  )}
                </div>
                {selectedUserName && (
                  loadingDiario ? (
                    <div className="h-64 rounded-xl border border-gray-200 bg-white flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                        <p className="text-gray-600 text-sm">Cargando datos diarios…</p>
                      </div>
                    </div>
                  ) : errorDiario ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                      <div className="text-red-600 font-medium mb-1">Error</div>
                      <div className="text-red-700 text-sm">{errorDiario}</div>
                    </div>
                  ) : (
                    <DailyMetricChart
                      datosProcesados={userSeriesDiario}
                      metricFilter={metric}
                      metricas={METRICAS}
                      viewType={viewType}
                      yearMonth={{ year: currentYear, monthIndex0: currentMonth0 }}
                    />
                  )
                )}
              </>
            )}
          </div>

          {/* Lista de usuarios (Comp/Hab del mes presente + Mes + Estado) */}
          {showUserList && (() => {
            // agrupar por usuario_id sobre *filteredRows* (mensual)
            const byUser = new Map();
            (filteredRows || []).forEach(r => {
              const uid = String(r.usuario_id ?? r.user_id ?? r.id_usuario ?? r.id ?? "");
              if (!uid) return;
              const keyMes = r.mes;
              if (!byUser.has(uid)) {
                byUser.set(uid, {
                  usuario_id: uid,
                  gradohr: r.grado_humanreadable,
                  categoria: r.categoria_usuario,
                  given_name: r.given_name || "—",
                  family_name: r.family_name || "",
                  meses: new Map(),
                });
              }
              byUser.get(uid).meses.set(keyMes, r);
            });


            const usersAggregated = Array.from(byUser.values()).map(u => {
              const rowCur = u.meses.get(MONTH_KEY);
              const comp = Number(rowCur?.comprension_lectora ?? 0) * 100;
              const hab  = Number(rowCur?.inorm ?? 0) * 100;
              const score = (comp + hab) / 2;
              // estado por score
              let estado = { texto: "PREINICIO", clase: "bg-red-100 text-red-800" };
              if (score < 45) estado = { texto: "PREINICIO", clase: "bg-red-100 text-red-800" };
              else if (score < 65) estado = { texto: "INICIO", clase: "bg-orange-100 text-orange-800" };
              else if (score < 80) estado = { texto: "EN PROGRESO", clase: "bg-yellow-100 text-yellow-800" };
              else if (score < 90) estado = { texto: "LOGRADO", clase: "bg-blue-100 text-blue-800" };
              else estado = { texto: "DESTACADO", clase: "bg-green-100 text-green-800" };

              return {
                usuario_id: u.usuario_id,
                name: displayName(u.given_name, u.family_name),
                categoria: u.categoria,
                gradohr: u.gradohr,
                comp, hab, score, estado
              };
            }).sort((a,b) => b.score - a.score);
            return (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 border-b px-3 py-2 text-sm font-medium flex justify-between items-center">
                  <span>Usuarios ({usersAggregated.length}) — Mes: {mesActualEtiqueta}</span>
                  <button onClick={() => setShowUserList(false)} className="text-xs text-gray-500 hover:text-gray-700">✕</button>
                </div>
                <div className="max-h-72 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-gray-500">
                        <th className="text-left  py-2 px-2">Nombre</th>
                        <th className="text-left  py-2 px-2">Categoría</th>
                        <th className="text-left  py-2 px-2">Grado</th>
                        <th className="text-right py-2 px-2">ICL(Comprensión)</th>
                        <th className="text-right py-2 px-2">IHL(Hábito)</th>
                        <th className="text-center py-2 px-2">Mes</th>
                        <th className="text-center py-2 px-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersAggregated.slice(0, 200).map(u => {
                        const isSelected = String(selectedUserId || "") === String(u.usuario_id);
                        return (
                          <tr key={u.usuario_id}
                              className={`border-t hover:bg-blue-50 cursor-pointer transition-colors ${isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : ""}`}
                              onClick={() => { setSelectedUserId(u.usuario_id); setSelectedUserName(u.name); setTab("diario"); }}
                              title={`Ver evolución de ${u.name}`}>
                            <td className="py-2 px-2 font-medium">
                              <div className="flex items-center gap-1"><span>👤</span><span>{u.name}</span></div>
                            </td>
                            <td className="py-2 px-2 font-medium">
                              <div className="flex items-center gap-1"><span>👤</span><span>{u.categoria}</span></div>
                            </td>
                            <td className="py-2 px-2 font-medium">
                              <div className="flex items-center gap-1"><span>{u.gradohr}</span></div>
                            </td>
                            <td className="py-2 px-2 text-right font-semibold">
                              <span className={u.comp >= 70 ? "text-green-600" : u.comp >= 50 ? "text-yellow-600" : "text-red-600"}>
                                {u.comp.toFixed(1)}%
                              </span>
                            </td>
                            <td className="py-2 px-2 text-right font-semibold">
                              <span className={u.hab >= 70 ? "text-green-600" : u.hab >= 50 ? "text-yellow-600" : "text-red-600"}>
                                {u.hab.toFixed(1)}%
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">{mesActualEtiqueta}</td>
                            <td className="py-2 px-2 text-center">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-semibold ${u.estado.clase}`}>
                                {u.estado.texto}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {usersAggregated.length === 0 && (
                        <tr><td colSpan={5} className="py-6 text-center text-gray-500">No hay usuarios para los filtros aplicados.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}


/* =========================================================
   FilterBar
========================================================= */
export const FilterBar = memo(function FilterBar({
  nomProv,
  nivelFilter,
  onNivelFilterChange,
  lecturaFilter,
  onLecturaFilterChange,
  selectedDistrito,
  onClearSelection,
  q,
  onQChange,
  onReset,
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-4 bg-white border-b border-gray-200">
      <div className="flex flex-col gap-3 w-full">
        <button onClick={onReset} className="px-3 py-1.5 text-xs rounded-md bg-gray-100 hover:bg-gray-200">Restablecer filtros</button>

        <input
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Buscar colegio o distrito…"
          className="px-3 py-1.5 text-sm border rounded-md w-64"
        />

        {/* Nivel */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Filtrar por nivel:</span>
          <div className="flex flex-wrap gap-2">
            {["todos", "INICIAL", "PRIMARIA", "SECUNDARIA"].map((nivel) => (
              <button
                key={nivel}
                onClick={() => onNivelFilterChange(nivel)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  nivelFilter === nivel
                    ? "bg-blue-100 text-blue-700 border border-blue-200 shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
                }`}
              >
                {nivel === "todos" ? "Todos" : nivel}
              </button>
            ))}
          </div>
        </div>

        {/* Lectura */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Lectura:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "todos", label: "Todos" },
              { key: "leyeron", label: "Leyeron (Usuarios_Activos > 0)" },
              { key: "no_leyeron", label: "No leyeron (Usuarios_Activos = 0)" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => onLecturaFilterChange(opt.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  lecturaFilter === opt.key
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <span className="text-sm text-gray-600 hidden sm:block">
          Provincia: <strong>{nomProv}</strong>
        </span>
        {selectedDistrito && (
          <button
            onClick={onClearSelection}
            className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm font-medium border border-gray-300 transition-colors"
          >
            ✕ Limpiar selección
          </button>
        )}
      </div>
    </div>
  );
});

/* =========================================================
   MapaProvincia (export default)
========================================================= */
export default function MapaProvincia({
  nomProv = "SIHUAS",
  sampleData = [],
  height = "500px",
}) {
  const [colegioFocus, setColegioFocus] = useState("");

  const ls = (k, def) => {
    try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : def; }
    catch { return def; }
  };

  const [q, setQ] = useState("");
  const qDeferred = useDeferredValue(q);

  const [selected, setSelected] = useState(() => ls("selectedDistrito", null));
  const [nivelFilter, setNivelFilter] = useState(() => ls("nivelFilter", "todos"));
  const [lecturaFilter, setLecturaFilter] = useState(() => ls("lecturaFilter", "todos"));
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const onNivelFilterChange = useCallback((v) => setNivelFilter(v), []);
  const onLecturaFilterChange = useCallback((v) => setLecturaFilter(v), []);
  const onQChange = useCallback((v) => setQ(v), []);
  const onReset = useCallback(() => {
    setNivelFilter("todos");
    setLecturaFilter("todos");
    setSortBy("IGL");
    setSortDir("desc");
    setSelected(null);
    localStorage.removeItem("nivelFilter");
    localStorage.removeItem("lecturaFilter");
    localStorage.removeItem("sortBy");
    localStorage.removeItem("sortDir");
    localStorage.removeItem("selectedDistrito");
  }, []);

  const [sortBy, setSortBy] = useState(() => ls("sortBy", "IGL"));
  const [sortDir, setSortDir] = useState(() => ls("sortDir", "desc"));

  useEffect(() => { localStorage.setItem("nivelFilter", JSON.stringify(nivelFilter)); }, [nivelFilter]);
  useEffect(() => { localStorage.setItem("lecturaFilter", JSON.stringify(lecturaFilter)); }, [lecturaFilter]);
  useEffect(() => { localStorage.setItem("sortBy", JSON.stringify(sortBy)); }, [sortBy]);
  useEffect(() => { localStorage.setItem("sortDir", JSON.stringify(sortDir)); }, [sortDir]);
  useEffect(() => { localStorage.setItem("selectedDistrito", JSON.stringify(selected)); }, [selected]);

  // carga geojson
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("https://img-dinoblanco-shs.s3.us-east-1.com/c/geo/peru_distrital_simple.geojson"
          .replace(".com/", ".com/") // por si copian/pegan en distintos buckets
          .replace(".com", ".com")); // noop, placeholder seguro
        const fullGeoJSON = await response.json();
        if (!alive) return;
        const provinciaFeatures = (fullGeoJSON?.features || []).filter(
          (feature) => feature?.properties?.NOMBPROV === nomProv
        );
        if (provinciaFeatures.length === 0) {
          throw new Error(`No se encontraron distritos para la provincia: ${nomProv}`);
        }
        setGeoData({ type: "FeatureCollection", features: provinciaFeatures });
      } catch (err) {
        setError(err?.message || "Error cargando mapa");
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nomProv]);

  // KPIs generales
  const { overallStats } = useMemo(() => {
    const grouped = sampleData.reduce((acc, r) => {
      const key = r.distrito;
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});
    let totalICP = 0, totalIGL = 0, distCount = 0, totalColegios = 0;
    Object.values(grouped).forEach((rows) => {
      const igl = rows.reduce((s, r) => s + (Number(r.IGL) || 0), 0) / (rows.length || 1);
      const icp = rows.reduce((s, r) => s + (Number(r.ICP) || 0), 0) / (rows.length || 1);
      totalIGL += igl; totalICP += icp; distCount += 1; totalColegios += rows.length;
    });
    return {
      overallStats: {
        avgICP: (distCount ? totalICP / distCount : 0).toFixed(1),
        avgIGL: (distCount ? totalIGL / distCount : 0).toFixed(1),
        totalColegios,
        totalDistritos: distCount,
      },
    };
  }, [sampleData]);
  // filtrado + búsqueda + lectura
  const datosFiltrados = useMemo(() => {
    let filteredData = sampleData;
    if (nivelFilter !== "todos") filteredData = filteredData.filter((d) => d.nivel === nivelFilter);
    if (selected) filteredData = filteredData.filter((d) => d.distrito === selected);
    if (lecturaFilter === "leyeron") filteredData = filteredData.filter((d) => Number(d.Usuarios_Activos ?? 0) > 0);
    else if (lecturaFilter === "no_leyeron") filteredData = filteredData.filter((d) => Number(d.Usuarios_Activos ?? 0) === 0);
    const term = (qDeferred || "").trim().toLowerCase();
    if (term) {
      filteredData = filteredData.filter(
        (d) => (d.colegio ?? "").toLowerCase().includes(term) || (d.distrito ?? "").toLowerCase().includes(term)
      );
    }
    return filteredData;
  }, [sampleData, nivelFilter, selected, lecturaFilter, qDeferred]);

  const datosOrdenados = useMemo(() => {
    const accessors = {
      nivel: (r) => r.nivel ?? "",
      distrito: (r) => r.distrito ?? "",
      colegio: (r) => r.colegio ?? "",
      Usuarios_Totales: (r) => Number(r.Usuarios_Totales ?? 0),
      Usuarios_Activos: (r) => Number(r.Usuarios_Activos ?? 0),
      ICP: (r) => Number(r.ICP ?? 0),
      IHL: (r) => Number(r.IHL ?? 0),
      IGL: (r) => Number(r.IGL ?? 0),
      IGL_all: (r) => Number(r.IGL_all ?? 0),
      Estado: (r) => Number(r.IGL ?? 0),
    };
    const acc = accessors[sortBy] ?? ((r) => r[sortBy]);
    const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
    const compare = (a, b) => {
      const va = acc(a), vb = acc(b);
      const bothNumbers = typeof va === "number" && typeof vb === "number";
      let res = 0;
      if (bothNumbers) res = va === vb ? 0 : va < vb ? -1 : 1;
      else res = collator.compare(String(va ?? ""), String(vb ?? ""));
      return sortDir === "asc" ? res : -res;
    };
    return [...datosFiltrados].sort(compare);
  }, [datosFiltrados, sortBy, sortDir]);

  const currentStats = useMemo(() => {
    if (!selected) return null;
    const distritoData = datosFiltrados.filter((d) => d.distrito === selected);
    if (!distritoData.length) return null;
    const ICP = (distritoData.reduce((s, r) => s + (Number(r.ICP) || 0), 0) / distritoData.length).toFixed(1);
    const IGL = (distritoData.reduce((s, r) => s + (Number(r.IGL) || 0), 0) / distritoData.length).toFixed(1);
    return { ICP, IGL, total: distritoData.length };
  }, [selected, datosFiltrados]);

  const handleClearSelection = () => setSelected(null);
  const handleSort = (key) => {
    setSortBy((prevKey) => {
      if (prevKey === key) {
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir("asc");
      return key;
    });
  };

  const SortHeader = ({ label, colKey, alignRight = false }) => (
    <button
      type="button"
      onClick={() => handleSort(colKey)}
      className={`flex items-center gap-1 group ${alignRight ? "justify-end w-full" : ""}`}
      title="Ordenar"
    >
      <span>{label}</span>
      <span className="text-gray-400 group-hover:text-gray-600">
        {sortBy === colKey ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </button>
  );

  function EnhancedDistricts({ data, selected, onSelect, nivelFilter, sampleData }) {
    const map = useMap();

    useEffect(() => {
      if (!data || !map || !data.features?.length) return;
      const gj = L.geoJSON(data);
      map.fitBounds(gj.getBounds(), { padding: [30, 30] });
      gj.remove();
    }, [data, map]);

    const getLabelPosition = (feature) => {
      try {
        // centroid simple para polígonos (asume MultiPolygon/Polygon)
        const coords = feature.geometry.type === "Polygon"
          ? feature.geometry.coordinates[0]
          : feature.geometry.coordinates[0][0];
        let sumLat = 0, sumLng = 0;
        coords.forEach((c) => { sumLng += c[0]; sumLat += c[1]; });
        return [sumLat / coords.length, sumLng / coords.length];
      } catch {
        // fallback
        return [-9.2, -77.5];
      }
    };

    const getFilteredIGL = (distrito) => {
      let distr = sampleData.filter((d) => d.distrito === distrito);
      if (nivelFilter !== "todos") distr = distr.filter((d) => d.nivel === nivelFilter);
      if (!distr.length) return "–";
      const igl = distr.reduce((s, r) => s + (Number(r.IGL) || 0), 0) / distr.length;
      return igl.toFixed(1);
    };

    const getDistrictStyle = (feature) => {
      const distrito = feature?.properties?.NOMBDIST;
      const isSelected = distrito === selected;
      const iglStr = getFilteredIGL(distrito);
      let color = "#6b7280";
      const iglNum = parseFloat(iglStr);
      if (!Number.isNaN(iglNum)) {
        if (iglNum >= 90) color = "#10b981";
        else if (iglNum >= 80) color = "#3b82f6";
        else if (iglNum >= 65) color = "#f59e0b";
        else if (iglNum >= 45) color = "#f97316";
        else if (iglNum > 0) color = "#ef4444";
      }
      return {
        color: isSelected ? "#111827" : color,
        weight: isSelected ? 3 : 2,
        opacity: isSelected ? 1 : 0.8,
        fillColor: color,
        fillOpacity: isSelected ? 0.15 : 0.08,
        dashArray: isSelected ? undefined : "2 4",
        lineCap: "round",
        lineJoin: "round",
      };
    };

    const layerRef = useRef(null);
    const markersRef = useRef([]);

    useEffect(() => {
      // limpiar labels previos al re-renderizar
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    });

    const onEachFeature = (feature, layer) => {
      const distrito = feature.properties.NOMBDIST;
      const igl = getFilteredIGL(distrito);
      const center = getLabelPosition(feature);

      const label = L.divIcon({
        html: `
          <div style="
            pointer-events:none;
            font-size:10px;
            color:#111827;
            text-shadow:0 1px 1px rgba(255,255,255,.9);
            text-align:center;
            line-height:1.1;
          ">
            <div style="font-weight:600">${distrito}</div>
            <div style="font-weight:700">IGL: ${igl}</div>
          </div>
        `,
        className: "custom-district-label",
        iconSize: [80, 40],
        iconAnchor: [40, 20],
      });

      const marker = L.marker(center, { icon: label, interactive: false });
      marker.addTo(layer._map);
      markersRef.current.push(marker);

      layer.on({
        click: (e) => { onSelect(distrito); L.DomEvent.stopPropagation(e); },
        mouseover: () => { layer.setStyle({ weight: 3, fillOpacity: 0.2, color: "#111827" }); },
        mouseout: () => { layer.setStyle(getDistrictStyle(feature)); },
      });
    };

    if (!data || !data.features?.length) return null;
    return (
      <GeoJSON
        ref={layerRef}
        data={data}
        style={getDistrictStyle}
        onEachFeature={onEachFeature}
        smoothFactor={1.5}
      />
    );
  }

  const SubtleTileLayer = () => (
    <TileLayer
      url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      opacity={0.3}
    />
  );

  const downloadCSV = (rows, filename = "colegios.csv") => {
    if (!rows?.length) return;
    const headers = ["Nivel","Distrito","Colegio","Usuarios","Lectores","ICP","IHL","IGL","Estado"];
    const toEstado = (igl) =>
      igl >= 90 ? "Destacado" : igl >= 80 ? "LOGRADO" : igl >= 65 ? "EN PROGRESO" : igl >= 45 ? "INICIO" : "PREINICIO";
    const lines = [
      headers.join(","),
      ...rows.map((r) =>
        [
          `"${r.nivel || ""}"`,
          `"${r.distrito || ""}"`,
          `"${(r.colegio || "").replace(/"/g, '""')}"`,
          Number(r.Usuarios_Totales ?? 0),
          Number(r.Usuarios_Activos ?? 0),
          Number(r.ICP ?? 0),
          Number(r.IHL ?? 0),
          Number(r.IGL ?? 0),
          Number(r.IGL_all ?? 0),
          `"${toEstado(Number(r.IGL ?? 0))}"`,
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 space-y-6">
      {/* Header + KPIs */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6 mb-2 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-xl shadow-sm border border-blue-100">
                <span className="text-2xl text-blue-600">📊</span>
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Mapa Educativo — {nomProv}</h1>
                <p className="text-gray-600 mt-1">Monitoreo de comprensión y hábito lector</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3 min-w-max">
            <div className="flex items-center gap-3 bg-white rounded-xl border p-3">
              <div>🏫</div>
              <div>
                <div className="text-xl font-bold">{overallStats.totalColegios}</div>
                <div className="text-xs text-gray-500">Colegios</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl border p-3">
              <div>📈</div>
              <div>
                <div className="text-xl font-bold">{overallStats.avgIGL}</div>
                <div className="text-xs text-gray-500">IGL Promedio</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de filtros */}
      <FilterBar
        nomProv={nomProv}
        nivelFilter={nivelFilter}
        onNivelFilterChange={onNivelFilterChange}
        lecturaFilter={lecturaFilter}
        onLecturaFilterChange={onLecturaFilterChange}
        selectedDistrito={selected}
        onClearSelection={handleClearSelection}
        q={q}
        onQChange={onQChange}
        onReset={onReset}
      />

            {/* Tabla de colegios */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
          <h3 className="text-md font-semibold text-gray-900">
            {selected ? `Colegios — ${selected}` : `Todos los Colegios — ${nomProv}`}
            {nivelFilter !== "todos" && ` (${nivelFilter})`}
          </h3>
          <button
            onClick={() => downloadCSV(datosOrdenados)}
            className="no-print flex items-center gap-2 px-3 py-2 bg-white border border-emerald-200 rounded-lg text-sm text-emerald-700 hover:bg-emerald-50"
          >
            ⬇️ Exportar CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <div
                className="overflow-y-auto"
                style={{ "--row-h": "44px", maxHeight: "calc(3.5 * var(--row-h))" }}
            >
          <table className="min-w-full table-fixed">
      <thead className="bg-gray-50">
        <tr>
          {[
            { label: "Nivel", key: "nivel", align: "text-left" },
            { label: "Distrito", key: "distrito", align: "text-left" },
            { label: "Colegio", key: "colegio", align: "text-left" },
            { label: "Usuarios", key: "Usuarios_Totales", align: "text-right", ar: true },
            { label: "Lectores", key: "Usuarios_Activos", align: "text-right", ar: true },
            { label: "ICP", key: "ICP", align: "text-right", ar: true },
            { label: "IHL", key: "IHL", align: "text-right", ar: true },
            { label: "IGL", key: "IGL", align: "text-right", ar: true },
            { label: "NOTA GLOBAL", key: "IGL_all", align: "text-right", ar: true },
            { label: "Estado", key: "Estado", align: "text-center" },
          ].map((col) => (
            <th
              key={col.key}
              className={`px-2 py-2 uppercase text-xs font-semibold tracking-wider ${col.align}
                          sticky top-0 z-10 bg-gray-50`}
            >
              <SortHeader label={col.label} colKey={col.key} alignRight={col.ar} />
            </th>
          ))}
        </tr>
      </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {datosOrdenados.length > 0 ? (
                datosOrdenados.map((r, idx) => {
                  const estado =
                    r.IGL >= 90 ? { texto: "Destacado", color: "bg-green-100 text-green-800" } :
                    r.IGL >= 80 ? { texto: "LOGRADO", color: "bg-blue-100 text-blue-800" } :
                    r.IGL >= 65 ? { texto: "EN PROGRESO", color: "bg-yellow-100 text-yellow-800" } :
                    r.IGL >= 45 ? { texto: "INICIO", color: "bg-orange-100 text-orange-800" } :
                    { texto: "PREINICIO", color: "bg-red-100 text-red-800" };

                  return (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${selected === r.distrito ? "bg-blue-25 border-l-2 border-l-blue-500" : ""}`}
                      onClick={() => {  if (r.colegio) setColegioFocus(r.colegio); }}
                    >
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-semibold ${
                          r.nivel === "INICIAL" ? "bg-pink-100 text-pink-700"
                          : r.nivel === "PRIMARIA" ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                        }`}>
                          {r.nivel?.slice(0, 3)}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className="text-sm text-gray-800 font-medium">{r.distrito}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-sm text-gray-700 line-clamp-1" title={r.colegio}>{r.colegio}</span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-gray-900">{r.Usuarios_Totales}</span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-gray-900">{r.Usuarios_Activos}</span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${
                          r.ICP >= 90 ? "text-green-600" : r.ICP >= 80 ? "text-blue-600" : r.ICP >= 65 ? "text-yellow-600"
                          : r.ICP >= 45 ? "text-orange-600" : "text-red-600"
                        }`}>
                          {r.ICP}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${
                          r.IHL >= 90 ? "text-green-600" : r.IHL >= 80 ? "text-blue-600" : r.IHL >= 65 ? "text-yellow-600"
                          : r.IHL >= 45 ? "text-orange-600" : "text-red-600"
                        }`}>
                          {r.IHL}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${
                          r.IGL >= 90 ? "text-green-600" : r.IGL >= 80 ? "text-blue-600" : r.IGL >= 65 ? "text-yellow-600"
                          : r.IGL >= 45 ? "text-orange-600" : "text-red-600"
                        }`}>
                          {r.IGL}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${
                          r.IGL_all >= 90 ? "text-green-600" : r.IGL_all >= 80 ? "text-blue-600" : r.IGL_all >= 65 ? "text-yellow-600"
                          : r.IGL_all >= 45 ? "text-orange-600" : "text-red-600"
                        }`}>
                          {r.IGL_all}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-1 py-0.5 rounded-full text-xs font-semibold ${estado.color}`}>
                          {estado.texto}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-gray-500 text-sm">
                    No se encontraron colegios con los filtros aplicados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

        </div>
      </div>



      <div className="lg:col-span-1">
        <MonthlyReaderPanel
          dataUrl="https://img-dinoblanco-shs.s3.us-east-1.amazonaws.com/c/r/res_usuarios_mensual.json"
          dataUrlDiaria="https://img-dinoblanco-shs.s3.us-east-1.amazonaws.com/c/r/res_usuarios_diariomesactual.json"
          externalColegio={colegioFocus}
          defaultNivel="todos"
          nomProv={nomProv}
        />
      </div>

    </div>
  );
}
